import React from "react";
import { Box, HStack, Tag, Text, VStack, Collapse } from "@chakra-ui/react";
import { formatMb, formatBytes, formatDuration, formatTime } from "./format";
import { formatMoment } from "./memoryChartData";

const NEAR_LIMIT_RATIO = 0.85;

function requestLine(r) {
  const who = r.user_email || r.user_id || "no signed-in user";
  const extras = [];
  if (r.resource?.file) extras.push(r.resource.file);
  if (r.resource?.room) extras.push(r.resource.room);
  if (r.resource?.pages) extras.push(`${r.resource.pages} pages`);
  return `${who} · ${r.path} · took ${formatDuration(r.duration_ms)}${extras.length ? " · " + extras.join(" · ") : ""}`;
}

function resourceLine(res) {
  const kind = { room: "document held in memory", file: "file", patient: "patient AI cache" }[res.kind] || res.kind;
  const bits = [kind];
  if (res.bytes) bits.push(formatBytes(res.bytes));
  if (res.pages) bits.push(`${res.pages} pages`);
  return `${res.key} (${bits.join(", ")})`;
}

function GaugesLine({ gauges, when }) {
  const g = gauges || {};
  return (
    <Text fontSize="sm" mb={2}>
      {when} the server held {g.collab_rooms ?? "—"} open document{g.collab_rooms === 1 ? "" : "s"} in memory
      ({formatBytes(g.collab_snapshot_bytes)}), the AI document cache was {g.document_cache_chars ? formatBytes(g.document_cache_chars) : "empty"},
      and {g.in_flight_requests ?? "—"} request{g.in_flight_requests === 1 ? " was" : "s were"} running at once.
    </Text>
  );
}

function Involved({ users, resources, requests, byId, overlapping }) {
  const hits = (overlapping || []).map((id) => byId.get(id)).filter(Boolean);
  return (
    <>
      {users && users.length > 0 && (
        <Text fontSize="sm"><b>People involved:</b> {users.join(", ")}</Text>
      )}
      {resources && resources.length > 0 && (
        <Text fontSize="sm"><b>Files and documents:</b> {resources.map(resourceLine).join("; ")}</Text>
      )}
      {hits.length > 0 ? (
        <>
          <Text fontSize="sm" mt={1}>Requests running while memory grew, with how much it grew during each:</Text>
          <VStack align="stretch" spacing={1}>
            {hits.map((r) => (
              <Text key={r.id} fontSize="sm">
                <b>{r.rss_delta_mb != null ? `${r.rss_delta_mb > 0 ? "+" : ""}${Math.round(r.rss_delta_mb)} MB` : "—"}</b>
                {" · "}{requestLine(r)}
              </Text>
            ))}
          </VStack>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Requests that overlap share the same growth, so they are leads rather than exact blame. A jump right after a
            restart is usually the server loading its libraries on the first requests.
          </Text>
        </>
      ) : (
        <Text fontSize="sm" color="gray.500">
          No recorded request overlapped this moment — look at the open documents and AI cache numbers above.
        </Text>
      )}
    </>
  );
}

/** The chip for one event, naming its server when that's known. */
function chipText(e, server) {
  const at = formatTime(e.t);
  if (e.kind === "restart" && e.deploy) return server ? `Deploy of ${server} at ${at}` : `Deploy at ${at}`;
  if (e.kind === "restart") return `${server || "Server"} restarted at ${at}${e.likelyOom ? " · likely out of memory" : ""}`;
  return `+${Math.round(e.delta_mb)} MB${server ? ` on ${server}` : ""} at ${at}${e.near_limit ? " · near limit" : ""}${e.crash_suspect ? " · before a restart" : ""}`;
}

/** Builds the unified, newest-first event list the chart markers and this list share. */
export function buildEvents(spikes, restarts, limitMb) {
  const jumps = (spikes || []).map((s) => ({
    key: `jump:${s.t}`, kind: "jump", t: s.t, y: s.rss_after_mb, ...s,
    label: `+${Math.round(s.delta_mb)} MB memory jump${s.near_limit ? " — near the limit" : ""}${s.crash_suspect ? " — just before a restart" : ""}`,
  }));
  const boots = (restarts || []).map((r) => {
    const ownLimit = r.limit_mb ?? limitMb;   // each server's own limit, when the backend sends it
    // A new process that took over before the old one stopped: planned, whatever memory it was at.
    const deploy = r.reason === "deploy";
    const likelyOom = !deploy && !!(ownLimit && r.rss_before_mb != null && r.rss_before_mb >= NEAR_LIMIT_RATIO * ownLimit);
    let label = "Server restarted — possible crash or deploy";
    if (deploy) label = "Deploy — a new server took over";
    else if (likelyOom) label = "Server restarted — likely out of memory";
    return {
      key: `restart:${r.t}`, kind: "restart", t: r.t, y: r.rss_before_mb, ...r, likelyOom, ownLimit, deploy, label,
    };
  });
  return [...jumps, ...boots].sort((a, b) => Date.parse(b.t) - Date.parse(a.t));
}

/**
 * Memory jumps and restarts in the window. Each chip (and each chart marker) expands into a
 * plain-English account of what the server was holding, who was active, which files or
 * documents were involved, and which requests were running.
 */
export default function SpikeList({ spikes, restarts, requests, limitMb, selected, onSelect, coveredSince, serverLabels }) {
  const byId = new Map((requests || []).map((r) => [r.id, r]));
  const events = buildEvents(spikes, restarts, limitMb);
  // Which server each event happened on, by the chart's names ("This computer (…)", "notesai-dev").
  const serverOf = (e) => (e.group && serverLabels?.get(e.group)) || null;
  // The details come from the newest samples only; on a long range they don't reach its start.
  const sinceText = coveredSince ? ` since ${formatMoment(Date.parse(coveredSince))}` : "";
  const since = sinceText || " in this window";
  if (events.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" mt={2}>
        No memory jumps of 5 MB or more and no restarts{since}.
        {coveredSince ? " The chart above covers the whole range." : ""}
      </Text>
    );
  }
  return (
    <Box mt={3}>
      <Text fontSize="xs" color="gray.500" mb={1}>
        Memory jumps and restarts{sinceText} (click one to see who and what was involved)
      </Text>
      <HStack spacing={2} flexWrap="wrap">
        {events.map((e) => (
          <Tag
            key={e.key}
            size="md"
            colorScheme={selected === e.t ? "purple" : e.kind === "restart" || e.near_limit || e.crash_suspect ? "red" : "orange"}
            cursor="pointer"
            onClick={() => onSelect && onSelect(selected === e.t ? null : e.t)}
          >
            {chipText(e, serverOf(e))}
          </Tag>
        ))}
      </HStack>
      {events.map((e) => (
        <Collapse key={`${e.key}-detail`} in={selected === e.t} animateOpacity>
          <Box borderWidth={1} borderRadius="md" p={3} mt={2} bg="gray.50">
            {e.kind === "restart" && e.deploy ? (
              <>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  {serverOf(e) ? `${serverOf(e)}: a` : "A"} new server started at {formatTime(e.resumed_at)} while the old one was still running, and the old
                  one stopped at {formatTime(e.t)}, using {formatMb(e.rss_before_mb)}. The new one started at {formatMb(e.rss_after_mb)}.
                </Text>
                <Text fontSize="sm" mb={2}>
                  A handover like this is a deploy (or a restart from the Render dashboard), not a crash.
                </Text>
                <GaugesLine gauges={e.gauges} when="Just before it stopped," />
              </>
            ) : e.kind === "restart" ? (
              <>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  {serverOf(e) || "The server"} stopped reporting at {formatTime(e.t)} and came back {e.gap_s ? `${e.gap_s} seconds later ` : ""}at {formatTime(e.resumed_at)},
                  using {formatMb(e.rss_after_mb)} (it was using {formatMb(e.rss_before_mb)} before).
                </Text>
                <Text fontSize="sm" mb={2}>
                  {e.likelyOom
                    ? `Memory was at ${Math.round((e.rss_before_mb / e.ownLimit) * 100)}% of the ${formatMb(e.ownLimit)} limit just before, so this was most likely an out-of-memory crash.`
                    : "This looks like a crash or a deploy. The details below show what was running in the two minutes before it stopped."}
                </Text>
                <GaugesLine gauges={e.gauges} when="Just before it stopped," />
              </>
            ) : (
              <>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  {serverOf(e) ? `On ${serverOf(e)}, app` : "App"} memory went from {formatMb(e.rss_before_mb)} to {formatMb(e.rss_after_mb)} at {formatTime(e.t)}
                  {e.near_limit ? `, which is within ${Math.round((1 - NEAR_LIMIT_RATIO) * 100)}% of the memory limit` : ""}
                  {e.crash_suspect ? ", and the server restarted shortly after" : ""}.
                </Text>
                <GaugesLine gauges={e.gauges} when="At that moment" />
              </>
            )}
            <Involved users={e.users} resources={e.resources} requests={requests} byId={byId} overlapping={e.overlapping} />
          </Box>
        </Collapse>
      ))}
    </Box>
  );
}
